import {
  Avatar,
  Box,
  Button,
  Divider,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  Stack,
  SvgIcon,
  Theme,
  Typography,
  useMediaQuery,
} from "@mui/material";
import {
  selectEmail,
  selectProfileImage,
  selectUserName,
} from "redux/authSlice";
import { useAppSelector } from "redux/hooks";
import PersonIcon from "@mui/icons-material/Person";
import SettingsIcon from "@mui/icons-material/Settings";
import SecurityIcon from "@mui/icons-material/Security";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import CustomPopper from "global/components/CustomPopper/CustomPopper";
import LogoutConfirmDialog from "global/components/LogoutConfirmDialog/LogoutConfirmDialog";
import { usePopover } from "hooks/use-popover";
import { useLogout } from "hooks/use-logout";
import { RouterLink } from "components/router-link";
import viewpaths from "global/constants/ViewPathConstants";
import Notification from "global/common/Notifications/Notification";
import { hasAccessTo, hasAccessToAccount } from "layouts/Config";
import strings from "global/constants/StringConstants";
import { focusRing } from "theme/colors";
import Preferences from "assets/icons1/Preferences.svg";
import GroupIcon from "@mui/icons-material/Group";

const menuRowSx = {
  borderRadius: 1,
  px: 1,
  py: 0.5,
  "&.Mui-focusVisible": {
    outline: (theme: Theme) => `2px solid ${theme.palette.primary.main}`,
    outlineOffset: "-2px",
  },
};

const AccountPopover = () => {
  const lgUp = useMediaQuery((theme: Theme) => theme.breakpoints.up("lg"));
  const mdUp = useMediaQuery((theme: Theme) => theme.breakpoints.up("md"));
  const userEmail = useAppSelector(selectEmail);
  const userName = useAppSelector(selectUserName);
  const popover = usePopover<any>();
  const profileImage = useAppSelector(selectProfileImage);
  const {
    isConfirmOpen,
    isLoggingOut,
    requestLogout,
    confirmLogout,
    cancelLogout,
  } = useLogout();

  const getMenuSection = () => {
    return (
      <>
        <Box sx={{ p: 2 }}>
          <Typography variant="body1">{userName}</Typography>
          <Typography
            color="text.secondary"
            variant="body2"
            sx={{ wordBreak: "break-word" }}
          >
            {userEmail}
          </Typography>
        </Box>
        <Divider />
        <Box sx={{ p: 1 }}>
          <ListItemButton
            href={viewpaths.settingsViewPath}
            onClick={popover.handleClose}
            sx={menuRowSx}
            LinkComponent={RouterLink}
          >
            <ListItemIcon>
              <SvgIcon fontSize="small">
                <SettingsIcon />
              </SvgIcon>
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography variant="body1">General Settings</Typography>
              }
            />
          </ListItemButton>
          {hasAccessTo(strings.resources.app, strings.adminPermission) && (
            <ListItemButton
              href={`${viewpaths.settingsViewPath}?tab=app-users`}
              onClick={popover.handleClose}
              sx={menuRowSx}
              LinkComponent={RouterLink}
            >
              <ListItemIcon>
                <SvgIcon fontSize="small">
                  <PersonIcon />
                </SvgIcon>
              </ListItemIcon>
              <ListItemText
                primary={<Typography variant="body1">App Users</Typography>}
              />
            </ListItemButton>
          )}
          {hasAccessTo(strings.resources.app, strings.adminPermission) && (
            <ListItemButton
              href={`${viewpaths.settingsViewPath}?tab=external-users`}
              onClick={popover.handleClose}
              sx={menuRowSx}
              LinkComponent={RouterLink}
            >
              <ListItemIcon>
                <SvgIcon fontSize="small">
                  <GroupIcon />
                </SvgIcon>
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography variant="body1">External Users</Typography>
                }
              />
            </ListItemButton>
          )}
          {hasAccessTo(strings.resources.app, strings.adminPermission) && (
            <ListItemButton
              href={`${viewpaths.settingsViewPath}?tab=role-management`}
              onClick={popover.handleClose}
              sx={menuRowSx}
              LinkComponent={RouterLink}
            >
              <ListItemIcon>
                <SvgIcon fontSize="small">
                  <ManageAccountsIcon />
                </SvgIcon>
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography variant="body1">Role Management</Typography>
                }
              />
            </ListItemButton>
          )}
          <ListItemButton
            href={`${viewpaths.settingsViewPath}?tab=security`}
            onClick={popover.handleClose}
            sx={menuRowSx}
            LinkComponent={RouterLink}
          >
            <ListItemIcon>
              <SvgIcon fontSize="small">
                <SecurityIcon />
              </SvgIcon>
            </ListItemIcon>
            <ListItemText
              primary={<Typography variant="body1">Security</Typography>}
            />
          </ListItemButton>
          {hasAccessToAccount() && (
            <ListItemButton
              href={`${viewpaths.settingsViewPath}?tab=account`}
              onClick={popover.handleClose}
              sx={menuRowSx}
              LinkComponent={RouterLink}
            >
              <ListItemIcon>
                <SvgIcon fontSize="small">
                  <ManageAccountsIcon />
                </SvgIcon>
              </ListItemIcon>
              <ListItemText
                primary={<Typography variant="body1">Account</Typography>}
              />
            </ListItemButton>
          )}
          <ListItemButton
            href={`${viewpaths.settingsViewPath}?tab=preferences`}
            onClick={popover.handleClose}
            sx={menuRowSx}
            LinkComponent={RouterLink}
          >
            <ListItemIcon>
              <img src={Preferences} alt="" aria-hidden="true" />
            </ListItemIcon>
            <ListItemText
              primary={<Typography variant="body1">Preferences</Typography>}
            />
          </ListItemButton>
        </Box>
        <Divider sx={{ my: "0 !important" }} />
        <Box
          sx={{
            display: "flex",
            p: 1,
            justifyContent: "center",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Button
            color="inherit"
            onClick={() => {
              popover.handleClose();
              requestLogout();
            }}
            size="medium"
          >
            Logout
          </Button>
        </Box>
      </>
    );
  };

  return (
    <Stack direction="row">
      <Notification />
      {lgUp && (
        <Stack sx={{ textAlign: "right", minWidth: 0, maxWidth: "200px" }} pr={1}>
          <Typography variant="body1" sx={{ wordBreak: "break-word", overflowWrap: "break-word" }}>
            {userName}
          </Typography>
          <Typography
            color="text.secondary"
            variant="body2"
            sx={{ fontSize: "0.75rem", wordBreak: "break-word", overflowWrap: "break-word" }}
          >
            {userEmail}
          </Typography>
        </Stack>
      )}
      <Box
        component="button"
        type="button"
        onClick={popover.handleOpen}
        ref={popover.anchorRef}
        aria-haspopup="dialog"
        aria-expanded={popover.open}
        aria-label={userName ? `Account menu, ${userName}` : "Account menu"}
        sx={{
          alignItems: "center",
          display: "flex",
          justifyContent: "center",
          borderWidth: 2,
          borderStyle: "solid",
          borderColor: mdUp ? "neutral.800" : "neutral.100",
          height: 40,
          width: 40,
          borderRadius: "50%",
          cursor: "pointer",
          p: 0,
          background: "transparent",
          "&:focus-visible": {
            outline: `2px solid ${focusRing}`,
            outlineOffset: "2px",
          },
        }}
      >
        <Avatar
          component="span"
          sx={{
            height: 32,
            width: 32,
          }}
          src={profileImage}
          alt={userName ? `${userName} profile picture` : "User profile picture"}
        />
      </Box>
      <CustomPopper
        open={popover.open}
        anchorRef={popover.anchorRef.current}
        handleClose={popover.handleClose}
        children={getMenuSection()}
        ariaLabel="Account menu"
      />
      <LogoutConfirmDialog
        isOpen={isConfirmOpen}
        isLoggingOut={isLoggingOut}
        onCancel={cancelLogout}
        onConfirm={confirmLogout}
      />
    </Stack>
  );
};

export default AccountPopover;
